/**
 * Merge a catalog item into another (BON-161). The picked item is the one that
 * SURVIVES; `source` is dissolved and its references re-point to the survivor.
 */
import { useEffect, useState } from 'react';

import {
  fetchCatalogItems,
  mergeCatalogItem,
  type CatalogItem,
} from '../../../api/inventory-catalog';

interface Props {
  source: CatalogItem;
  onClose: () => void;
  onMerged: () => void;
}

export default function MergeItemsModal({ source, onClose, onMerged }: Props) {
  const [candidates, setCandidates] = useState<CatalogItem[]>([]);
  const [survivingId, setSurvivingId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCatalogItems({ category: source.category, activeOnly: true, pageSize: 200 })
      .then((r) => setCandidates(r.items.filter((i) => i.id !== source.id)))
      .catch(() => setCandidates([]));
  }, [source.category, source.id]);

  async function handleMerge() {
    if (!survivingId) {
      setError('Pick the item to keep.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await mergeCatalogItem(source.id, survivingId);
      onMerged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-[#1e2521] rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100 mb-1">
          Merge item
        </h2>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">
          <span className="font-medium text-stone-700 dark:text-stone-300">{source.name}</span> will
          be retired and all its stock, submissions, and transfers re-pointed to the item you keep.
          Reversible within the unmerge window.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Keep this item
          </span>
          <select
            className="mt-1 block w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-moss-500"
            value={survivingId}
            onChange={(e) => setSurvivingId(e.target.value)}
          >
            <option value="">Select an item…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.sku})
              </option>
            ))}
          </select>
        </label>

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#242d28] transition"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-50"
          >
            {saving ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
